import { useEffect, useState } from 'react';

export interface UsePersistentStorageReturn {
  /** Whether persistent storage is supported */
  isSupported: boolean;
  /** Whether storage is currently persisted */
  isPersisted: boolean;
  /** Request persistent storage (call after user engagement) */
  requestPersistence: () => Promise<boolean>;
}

/**
 * Hook to manage persistent storage for the PWA.
 * Persistent storage prevents the browser from evicting cached data
 * (like audio files) when storage is low.
 */
export function usePersistentStorage(): UsePersistentStorageReturn {
  const [isSupported] = useState(() => {
    return typeof navigator !== 'undefined' &&
           'storage' in navigator &&
           'persist' in navigator.storage;
  });

  const [isPersisted, setIsPersisted] = useState(false);

  // Check current persistence status on mount
  useEffect(() => {
    if (!isSupported) return;

    navigator.storage.persisted().then((persisted) => {
      setIsPersisted(persisted);
    }).catch(() => {
      // Fails in sandboxed iframes — silently ignore
    });
  }, [isSupported]);

  // Request persistent storage
  const requestPersistence = async (): Promise<boolean> => {
    if (!isSupported) return false;

    try {
      const granted = await navigator.storage.persist();
      setIsPersisted(granted);

      if (granted) {
        console.log('Persistent storage granted');

        // Log storage estimate
        const estimate = await navigator.storage.estimate();
        const usedMB = ((estimate.usage || 0) / (1024 * 1024)).toFixed(2);
        const quotaMB = ((estimate.quota || 0) / (1024 * 1024)).toFixed(2);
        console.log(`Storage: ${usedMB}MB used of ${quotaMB}MB quota`);
      } else {
        console.log('Persistent storage denied');
      }

      return granted;
    } catch (error) {
      console.error('Failed to request persistent storage:', error);
      return false;
    }
  };

  return {
    isSupported,
    isPersisted,
    requestPersistence,
  };
}
